/**
 * The valves a channel's automation rules can drive.
 *
 * A rule's own history says whether the rule fired and whether the server
 * accepted its action — `actions_run: [{ type: 'relay_on', ok: true }]`. That
 * is not the same fact as the valve opening. `ok: true` means a SET_RELAY was
 * queued; whether the site ever collected it, and what it said back, lives in
 * `field_commands` and has never been visible from the sensor page.
 *
 * Which is the gap worth closing here: a tank rule that fires perfectly every
 * night against a bridge that stopped polling on Tuesday reads as a working
 * automation right up until somebody walks the block.
 */

import type { SensorRule, RuleAction } from '../services/sensorRules';

export interface RuleRelayTarget {
  controller_id: string;
  router_mac: string;
  relay_index: number;
  /** Every rule on this channel that drives the valve, by name. */
  rules: string[];
  /**
   * False only when every rule driving it is switched off. A valve one live
   * rule still reaches is a live valve, whatever the others are set to.
   */
  enabled: boolean;
}

/** MACs reach us in several spellings; the hex alone decides identity. */
const macKey = (m: unknown) => String(m ?? '').toUpperCase().replace(/[^0-9A-F]/g, '');

function drivesRelay(a: RuleAction): a is Extract<RuleAction, { relay_index: number }> {
  return a.type === 'relay_on' || a.type === 'relay_off';
}

/**
 * One entry per distinct valve, in a stable order.
 *
 * Deduped because `relay_on` and `relay_off` on the same valve are one valve,
 * not two — a fill rule almost always carries both, and listing its history
 * twice under two headings is the sort of duplicate that gets read as two
 * different outputs.
 */
export function ruleRelayTargets(rules: SensorRule[]): RuleRelayTarget[] {
  const byKey = new Map<string, RuleRelayTarget>();

  for (const rule of rules) {
    for (const action of rule.actions ?? []) {
      if (!drivesRelay(action)) continue;
      if (!action.controller_id || !action.router_mac) continue;

      const key = `${action.controller_id}|${macKey(action.router_mac)}|${action.relay_index}`;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.rules.includes(rule.name)) existing.rules.push(rule.name);
        existing.enabled = existing.enabled || rule.enabled;
      } else {
        byKey.set(key, {
          controller_id: action.controller_id,
          router_mac: action.router_mac,
          relay_index: action.relay_index,
          rules: [rule.name],
          enabled: rule.enabled,
        });
      }
    }
  }

  return [...byKey.values()].sort((a, b) =>
    a.controller_id.localeCompare(b.controller_id)
    || macKey(a.router_mac).localeCompare(macKey(b.router_mac))
    || a.relay_index - b.relay_index);
}
